import { useState, useCallback } from 'react';

import { useMutation } from '@zenbusiness/application-commons-graphql';
import { useCaptureException } from '@zenbusiness/application-commons-logging';

import { applyRenderStrategy } from '../components/utils/applyRenderStrategy';
import { useUnifiedBusinessEntityContext } from '../contexts/UnifiedBusinessEntityProvider';
import { SEND_FEEDBACK_MUTATION } from '../mutations/sendFeedback';
import { SUBMIT_USER_INPUT_MUTATION } from '../mutations/submitUserInputMutation';
import { buildThreadIdentity } from '../utils/buildThreadIdentity';

/**
 * Custom hook for instruction rendering using mutation-based approach
 *
 * This hook handles:
 * - Fetching existing messages (GET mode) on initial load
 * - Sending messages to the assistant API (POST mode) for user interactions
 * - Loading instructions one at a time
 * - Maintaining instruction state and completion status
 * - Applying render strategies to instructions
 * - Error handling
 * - Extracting and updating business entity UUID from GraphQL responses
 * - Thread navigation and state reset
 *
 * @returns {object} Hook state and methods
 */
export function useInstructionRenderer() {
  const { updateBusinessEntityUuidFromData } = useUnifiedBusinessEntityContext();

  // Initialize captureException with static tags for better performance
  const captureException = useCaptureException({
    tags: {
      frontend: 'assistant-ui',
      operationName: 'instructionRenderer'
    }
  });

  // State management
  const [instructions, setInstructions] = useState([]);
  const [currentInstructionCount, setCurrentInstructionCount] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [loadingNext, setLoadingNext] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [currentThreadUuid, setCurrentThreadUuid] = useState();
  const [currentMode, setCurrentMode] = useState('get'); // Track current request mode
  const [responseError, setResponseError] = useState(null); // Track response-level errors
  const [threadMode, setThreadMode] = useState(null); // Store thread mode from backend

  const [submitUserInput, { loading, error: mutationError }] = useMutation(
    SUBMIT_USER_INPUT_MUTATION
  );

  const [sendUserFeedbackMutation] = useMutation(SEND_FEEDBACK_MUTATION);

  /**
   * Reset all state when switching to a new thread
   */
  const resetStateForNewThread = useCallback((newThreadUuid) => {
    setInstructions([]);
    setCurrentInstructionCount(0);
    setIsComplete(false);
    setLoadingNext(false);
    setInitialized(false);
    setCurrentThreadUuid(newThreadUuid);
    setCurrentMode('get');
    setResponseError(null);
    setThreadMode(null);
  }, []);

  /**
   * Process and add new instructions to the existing array
   * @param {Array} currentInstructions - Current instructions array
   * @param {Array} newInstructionData - New instruction data from API
   * @returns {Array} Updated instructions array
   */
  const processAndAddInstructions = useCallback((currentInstructions, newInstructionData) => {
    let updatedInstructions = [...currentInstructions];

    newInstructionData.forEach((instruction) => {
      updatedInstructions = applyRenderStrategy(updatedInstructions, instruction);
    });

    return updatedInstructions;
  }, []);

  /**
   * Extract the messageUuid from the last assistant message in instructions
   * @param {Array} instructions - Current instructions array
   * @returns {string|null} The messageUuid of the last assistant message
   */
  // eslint-disable-next-line no-shadow
  const getLastMessageUuid = useCallback((instructions) => {
    // Look through instructions in reverse order to find the last assistant message
    // eslint-disable-next-line no-plusplus
    for (let i = instructions.length - 1; i >= 0; i--) {
      const instruction = instructions[i];
      if (instruction.id) {
        return instruction.id;
      }
    }
    return null;
  }, []);

  /**
   * Fetch existing messages for a thread (GET mode)
   * @param {object} uuids - Object containing visitUuid, userUuid, and/or businessEntityUuid
   * @param {string} existingThreadUuid - The thread UUID to fetch messages for
   * @param {string} currentUserMode - Current user mode (anonymous, starter, business, standalone)
   */
  const loadExistingMessages = useCallback(
    async ({ visitUuid, userUuid, businessEntityUuid }, existingThreadUuid, currentUserMode) => {
      if (loadingNext) {
        return;
      }

      setLoadingNext(true);
      setResponseError(null); // Clear previous errors

      try {
        // Build identity using the utility with the provided mode
        const { identity } = buildThreadIdentity(
          currentUserMode,
          visitUuid,
          userUuid,
          businessEntityUuid
        );

        // Use stored threadMode (from previous response) or fallback to current user mode
        const modeToSend = threadMode || currentUserMode;

        const response = await submitUserInput({
          variables: {
            identity,
            threadUuid: existingThreadUuid,
            mode: 'get',
            threadMode: modeToSend || undefined // Pass threadMode if available
          },
          onError: (err) => {
            captureException(err, {
              tags: {
                operationName: 'submitUserInput',
                errorType: 'graphql_mutation',
                threadUuid: existingThreadUuid || 'unknown',
                currentMode
              },
              contexts: {
                instructionRenderer: {
                  threadUuid: existingThreadUuid,
                  currentMode,
                  instructionCount: currentInstructionCount,
                  isComplete,
                  loadingNext,
                  initialized
                },
                mutationError: {
                  message: err.message,
                  networkError: err.networkError,
                  graphQLErrors: err.graphQLErrors
                },
                identity
              }
            });
          }
        });

        const responseData = response?.data?.interactWithAssistant;
        const existingInstructionData = responseData?.data || [];

        // Check for response-level errors (server errors that don't throw GraphQL errors)
        if (responseData && (!responseData.ok || responseData.error)) {
          const serverError = new Error('Server error in loadExistingMessages');
          captureException(serverError, {
            tags: {
              operationName: 'loadExistingMessages',
              errorType: 'server_error',
              threadUuid: existingThreadUuid,
              currentMode: 'get'
            },
            contexts: {
              instructionRenderer: {
                threadUuid: existingThreadUuid,
                currentMode: 'get',
                instructionCount: currentInstructionCount,
                isComplete,
                loadingNext,
                initialized
              },
              responseError: {
                ok: responseData.ok,
                error: responseData.error,
                meta: responseData.meta
              },
              identity
            }
          });

          // Set the response error state
          setResponseError(responseData.error || { message: 'Server error occurred' });

          // IMPORTANT: Set initialized to true even on error to prevent infinite loop
          setInitialized(true);
          return;
        }

        // Extract and update business entity UUID from GraphQL response
        // This is from loading existing messages, so it's from a thread
        updateBusinessEntityUuidFromData(existingInstructionData, true);
        setCurrentThreadUuid(existingThreadUuid);

        // Extract and store thread mode from backend metadata
        // This tells us what mode the thread was created in (business, starter, anonymous, standalone)
        if (responseData?.meta?.mode) {
          setThreadMode(responseData.meta.mode);
        }

        // Set instructions from response data
        setInitialized(true);

        if (existingInstructionData.length > 0) {
          // Replace current instructions with existing ones (not append)
          setInstructions(existingInstructionData);
          setCurrentInstructionCount(existingInstructionData.length);

          // Check if conversation is complete
          const hasCompletionIndicator = existingInstructionData.some(
            (instruction) => instruction.meta?.isComplete || instruction.type === 'completion'
          );

          if (hasCompletionIndicator) {
            setIsComplete(true);
          }
        } else {
          // No existing messages, need to trigger initial greet manually here
          // We'll handle this by setting a flag for the useEffect to handle
          // (setInitialized is already called above)
        }

        setIsComplete(responseData?.meta?.conversationComplete || false);
      } catch (err) {
        captureException(err, {
          tags: {
            operationName: 'loadExistingMessages',
            errorType: 'network_error',
            threadUuid: existingThreadUuid,
            currentMode: 'get'
          },
          contexts: {
            instructionRenderer: {
              threadUuid: existingThreadUuid,
              currentMode: 'get',
              instructionCount: currentInstructionCount,
              isComplete,
              loadingNext,
              initialized
            },
            responseError: {
              message: err.message,
              stack: err.stack
            },
            uuids: { visitUuid, userUuid, businessEntityUuid }
          }
        });

        // Set the response error state for catch block errors
        setResponseError({ message: err.message || 'An error occurred' });

        // IMPORTANT: Set initialized to true even on error to prevent infinite loop
        setInitialized(true);
      } finally {
        setLoadingNext(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      loadingNext,
      submitUserInput,
      updateBusinessEntityUuidFromData,
      currentInstructionCount,
      isComplete,
      initialized
      // threadUuid,
      // currentMode,

      // captureException
    ]
  );

  /**
   * Send a message to the assistant and load the next instruction (POST mode)
   * @param {string} messageContent - The message to send (defaults to 'continue')
   * @param {string} messageType - The type of message (defaults to 'text')
   * @param {string} mode - User mode (anonymous/starter/business/standalone)
   * @param {object} uuids - Object containing visitUuid, userUuid, businessEntityUuid, and optional agentName
   */
  const loadNextInstruction = useCallback(
    async (
      messageContent,
      messageType,
      mode,
      { visitUuid, userUuid, businessEntityUuid, agentName, tag, origination, sourceComponent } = {}
    ) => {
      if (isComplete || loadingNext) {
        return;
      }

      // Build identity using the utility
      const { identity } = buildThreadIdentity(mode, visitUuid, userUuid, businessEntityUuid);

      // Extract businessEntityUuid from identity if it exists (for input object)
      const beUuid = identity?.customer?.businessEntityUuid;

      // Get the current messageUuid from the last message in instructions
      const currentMessageUuid = getLastMessageUuid(instructions);

      setLoadingNext(true);
      setCurrentMode('post'); // Set mode to 'post' for new messages
      setResponseError(null); // Clear previous errors

      try {
        // Build the input object with agentName if provided
        const inputObject = {
          businessEntityUuid: beUuid,
          messageType,
          message: messageContent,
          role: 'user'
        };

        // Add agentName to input if provided
        if (agentName) {
          inputObject.agentName = agentName;
        }

        // Optional conversation context metadata (used by some flows like Standalone tools)
        if (tag) {
          inputObject.tag = tag;
        }
        if (origination) {
          inputObject.origination = origination;
        }
        if (sourceComponent) {
          inputObject.sourceComponent = sourceComponent;
        }

        const response = await submitUserInput({
          variables: {
            identity,
            input: inputObject,
            threadUuid: currentThreadUuid,
            messageUuid: currentMessageUuid,
            mode: 'post',
            threadMode: threadMode || undefined // Pass threadMode if available
          },
          onError: (err) => {
            captureException(err, {
              tags: {
                operationName: 'submitUserInput',
                errorType: 'graphql_mutation',
                threadUuid: currentThreadUuid || 'unknown',
                currentMode
              },
              contexts: {
                instructionRenderer: {
                  threadUuid: currentThreadUuid,
                  currentMode,
                  instructionCount: currentInstructionCount,
                  isComplete,
                  loadingNext,
                  initialized
                },
                mutationError: {
                  message: err.message,
                  networkError: err.networkError,
                  graphQLErrors: err.graphQLErrors
                },
                identity
              }
            });
          }
        });

        // console.log('[useInstructionRenderer] POST mode response:', response);

        const responseData = response?.data?.interactWithAssistant;
        const newInstructionData = responseData?.data || [];

        // Extract and update business entity UUID from GraphQL response
        // This is from sending new messages, so it's from a thread
        updateBusinessEntityUuidFromData(newInstructionData, true);

        // Check for response-level errors (server errors that don't throw GraphQL errors)
        if (responseData && (!responseData.ok || responseData.error)) {
          const serverError = new Error('Server error in response');

          captureException(serverError, {
            tags: {
              operationName: 'loadNextInstruction',
              errorType: 'server_error',
              threadUuid: currentThreadUuid,
              currentMode: 'post'
            },
            contexts: {
              instructionRenderer: {
                threadUuid: currentThreadUuid,
                currentMode: 'post',
                instructionCount: currentInstructionCount,
                isComplete,
                loadingNext,
                initialized
              },
              responseError: {
                ok: responseData.ok,
                error: responseData.error,
                meta: responseData.meta
              },
              identity
            }
          });

          // Set the response error state
          setResponseError(responseData.error || { message: 'Server error occurred' });

          // IMPORTANT: Set initialized to true even on error to prevent infinite loop
          setInitialized(true);

          // Throw the error so it can be caught by the calling code
          throw serverError;
        }

        setInitialized(true);

        if (newInstructionData.length > 0) {
          if (!currentThreadUuid && newInstructionData[0]?.attributes?.threadUuid) {
            setCurrentThreadUuid(newInstructionData[0].attributes.threadUuid);
          }

          setInstructions((currentInstructions) => {
            const updatedInstructions = processAndAddInstructions(
              currentInstructions,
              newInstructionData
            );
            return [...updatedInstructions];
          });

          setCurrentInstructionCount((prev) => prev + 1);

          const hasCompletionIndicator = newInstructionData.some(
            (instruction) => instruction.meta?.isComplete || instruction.type === 'completion'
          );

          if (hasCompletionIndicator) {
            setIsComplete(true);
          }
        } else if (instructions.length > 0) {
          setIsComplete(true);
        }
      } catch (err) {
        captureException(err, {
          tags: {
            operationName: 'loadNextInstruction',
            errorType: 'network_error',
            threadUuid: currentThreadUuid,
            currentMode: 'post'
          },
          contexts: {
            instructionRenderer: {
              threadUuid: currentThreadUuid,
              currentMode: 'post',
              instructionCount: currentInstructionCount,
              isComplete,
              loadingNext,
              initialized
            },
            responseError: {
              message: err.message,
              stack: err.stack
            },
            uuids: { visitUuid, userUuid, businessEntityUuid }
          }
        });

        // Set the response error state for catch block errors
        setResponseError({ message: err.message || 'An error occurred' });
      } finally {
        setLoadingNext(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isComplete,
      loadingNext,
      submitUserInput,
      currentThreadUuid,
      processAndAddInstructions,
      instructions,
      getLastMessageUuid,
      updateBusinessEntityUuidFromData,
      currentInstructionCount,
      initialized
      // currentMode,
      // captureException
    ]
  );

  /**
   * Add a UserResponse to the current conversation immediately (optimistic update)
   * @param {string} message - The user's message
   * @param {string} messageType - The type of user interaction
   * @param {string} instructionId - ID of the instruction group to add UserResponse to
   */
  const addUserResponse = useCallback((message, messageType, instructionId = null) => {
    const userResponseInstruction = {
      componentType: 'UserResponse',
      props: {
        message,
        messageType,
        role: 'user'
      }
    };

    setInstructions((currentInstructions) => {
      const updatedInstructions = [...currentInstructions];

      // If instructionId is provided, add to that specific instruction group
      if (instructionId) {
        const targetIndex = updatedInstructions.findIndex(
          (instruction) => instruction.id === instructionId
        );

        if (targetIndex !== -1) {
          updatedInstructions[targetIndex] = {
            ...updatedInstructions[targetIndex],
            attributes: {
              ...updatedInstructions[targetIndex].attributes,
              uiInstructions: [
                ...updatedInstructions[targetIndex].attributes.uiInstructions,
                userResponseInstruction
              ]
            }
          };
        }
      } else {
        // Add to the last instruction group if no specific ID provided
        // eslint-disable-next-line no-lonely-if
        if (updatedInstructions.length > 0) {
          const lastIndex = updatedInstructions.length - 1;
          updatedInstructions[lastIndex] = {
            ...updatedInstructions[lastIndex],
            attributes: {
              ...updatedInstructions[lastIndex].attributes,
              uiInstructions: [
                ...updatedInstructions[lastIndex].attributes.uiInstructions,
                userResponseInstruction
              ]
            }
          };
        }
      }

      return updatedInstructions;
    });
  }, []);

  /**
   * Add a VeloResponse to the current conversation immediately (optimistic update)
   * @param {object} uuids - Object containing visitUuid, userUuid, and/or businessEntityUuid
   * @param {string} messageUuid - The message UUID
   * @param {number} feedback - The feedback value
   * @param {string} mode - User mode (anonymous/starter/business/standalone)
   */
  const sendUserFeedback = useCallback(
    async ({ visitUuid, userUuid, businessEntityUuid }, messageUuid, feedback, mode) => {
      if (loadingNext) {
        return;
      }

      // Build identity using the utility
      const { identity } = buildThreadIdentity(mode, visitUuid, userUuid, businessEntityUuid);

      setLoadingNext(true);
      setResponseError(null); // Clear previous errors

      const feedbackResponse = await sendUserFeedbackMutation({
        variables: {
          identity,
          threadUuid: currentThreadUuid,
          messageUuid,
          feedback
        },
        onError: (err) => {
          captureException(err, {
            tags: {
              operationName: 'sendUserFeedback',
              errorType: 'graphql_mutation',
              threadUuid: currentThreadUuid || 'unknown',
              messageUuid: messageUuid || 'unknown',
              feedback: feedback || 'unknown'
            },
            contexts: {
              mutationError: {
                message: err.message,
                networkError: err.networkError,
                graphQLErrors: err.graphQLErrors
              },
              identity,
              input: {
                messageUuid,
                feedback
              }
            }
          });
        }
      });

      const feedbackResponseData = feedbackResponse?.data?.sendFeedback;

      // good feedback does not return a new message
      if (feedbackResponseData.ok) {
        setInstructions((currentInstructions) => {
          return currentInstructions.map((instruction) => {
            // Find and update the FeedbackForm component in uiInstruction
            if (instruction.id === messageUuid) {
              const updatedUiInstructions = instruction.attributes.uiInstructions.map((ui) => {
                if (ui.componentType === 'FeedbackForm') {
                  return {
                    ...ui,
                    props: {
                      ...ui.props,
                      value: feedback
                    }
                  };
                }
                return ui;
              });

              // Return updated instruction with new uiInstructions
              return {
                ...instruction,
                attributes: {
                  ...instruction.attributes,
                  uiInstructions: updatedUiInstructions
                }
              };
            }
            return instruction;
          });
        });
      }

      // bad feedback returns a new message
      const feedbackData = feedbackResponseData?.data;
      if (feedbackData && feedbackData.length > 0) {
        // IMPORTANT: Set initialized to true even on error to prevent infinite loop
        setInitialized(true);

        // Extract and update business entity UUID from GraphQL response
        // This is from feedback, so it's from a thread
        updateBusinessEntityUuidFromData(feedbackData, true);

        setInstructions((currentInstructions) => {
          const updatedInstructions = processAndAddInstructions(currentInstructions, feedbackData);
          return [...updatedInstructions];
        });

        // Update instruction count
        setCurrentInstructionCount((prev) => prev + feedbackData.length);
      }

      setLoadingNext(false);
    },
    [
      captureException,
      loadingNext,
      processAndAddInstructions,
      sendUserFeedbackMutation,
      currentThreadUuid,
      updateBusinessEntityUuidFromData
    ]
  );

  /**
   * Retry connection by reloading existing messages for the current thread
   * This function clears error state and attempts to reload the thread
   * @param {object} uuids - Object containing visitUuid, userUuid, and/or businessEntityUuid
   * @param {string} mode - User mode (anonymous/starter/business/standalone)
   */
  const retryConnection = useCallback(
    async ({ visitUuid, userUuid, businessEntityUuid }, mode) => {
      if (!currentThreadUuid) {
        return;
      }

      // Clear current instructions and reset state for retry
      setInstructions([]);
      setCurrentInstructionCount(0);
      setIsComplete(false);
      setInitialized(false);
      setResponseError(null); // Clear response errors

      // Attempt to reload existing messages
      await loadExistingMessages(
        { visitUuid, userUuid, businessEntityUuid },
        currentThreadUuid,
        mode
      );
    },
    [currentThreadUuid, loadExistingMessages]
  );

  // Handle case where we tried to fetch existing messages but found none - start with greet
  // TEMPORARILY COMMENTED OUT TO STOP INFINITE LOOP - WILL ADD BACK AFTER DEBUGGING
  // useEffect(() => {
  //   if (initialized && instructions.length === 0 && threadUuid && !loadingNext && !isComplete) {
  //     console.log(
  //       '[useInstructionRenderer] No existing messages found after GET, starting with greet'
  //     );
  //     loadNextInstruction('greet', 'greet');
  //   }
  // }, [initialized, instructions.length, threadUuid, loadingNext, isComplete]); // Removed loadNextInstruction from dependencies to prevent infinite loop

  return {
    // Data
    currentThreadUuid,
    instructions,
    threadMode, // Thread mode from backend (business, starter, anonymous, standalone)

    // State
    loading: loading && !initialized,
    loadingNext,
    isError: Boolean(mutationError || responseError),
    error: mutationError || responseError,
    isComplete,
    initialized,
    currentMode, // Expose current mode

    // Computed values
    currentIndex: currentInstructionCount,
    totalInstructions: currentInstructionCount,
    hasNextInstruction: !isComplete,

    // Methods
    loadNextInstruction,
    addUserResponse,
    loadExistingMessages,
    resetStateForNewThread,
    retryConnection,
    sendUserFeedback,
    setInstructions
  };
}
